import { Component, EventEmitter, NgZone, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterModule, RouterOutlet } from '@angular/router';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NavbarComponent } from './components/navbar/navbar.component';
import { FooterComponent } from './components/footer/footer.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from './services/auth.service';
import { Observable } from 'rxjs';
import { HttpService } from '../app/services/http.service';
import { LoadingComponent } from './components/loading/loading.component';
import { CookieConsentService } from './services/cookie-consent.service';
import { DataService } from './services/data.service';
import { StorageService } from './services/storage.service';
import { ConfigService } from './services/env-config.service';
import { UserAccountDTO } from './classes/user-account.dto';
import * as signalR from '@microsoft/signalr';
import { KeycloakService } from 'keycloak-angular';
import { GtmService } from './services/gtm.service';
import { NzModalModule, NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { ModalHandleLoginErrorsComponent } from './components/modals/modal-handle-login-errors/modal-handle-login-errors.component';
import { CompanySelectionModalComponent } from './components/modals/company-selection-modal/company-selection-modal.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    NzLayoutModule,
    NzMenuModule,
    NzIconModule,
    NzDividerModule,
    NzDropDownModule,
    NavbarComponent,
    SidebarComponent,
    FooterComponent,
    RouterModule,
    LoadingComponent,
    NzModalModule
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  isLogged: boolean = false;
  blackDress: Observable<any> | undefined;
  isCollapsed: boolean = false;
  showFooter: boolean = true;
  showNavbar: boolean = true;
  isPreviewMode: boolean = false;
  static onNotification = new EventEmitter<void>();
  connection?: signalR.HubConnection;


  constructor(
    private translateService: TranslateService,
    private authService: AuthService,
    private httpService: HttpService,
    private router: Router,
    private cookieConsentService: CookieConsentService,
    private storage: StorageService,
    private configService: ConfigService,
    private ngZone: NgZone,
    private keycloak: KeycloakService,
    private gtmService: GtmService,
    private modal: NzModalService,
    private dataService: DataService
  ) {
    this.translateService.setDefaultLang('it');
    this.translateService.use('it');
    this.translateService.onLangChange.subscribe((event) => {
      localStorage.setItem('lang', event.lang);
    });
    localStorage.setItem('lang', this.translateService.currentLang || 'it');
    const token = JSON.parse(localStorage.getItem('authToken')!);
    if (token) {
      this.authService.isAuthenticatedSubject.next(true);
    }
    this.authService.isAuthenticatedObservable().subscribe((logged: boolean) => {
      this.isLogged = logged;
      if (logged) {
        this.startSignalRConnection();
      } else {
        this.connection?.stop();
      }
    });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const currentRoute = event.urlAfterRedirects;
        const isOnMobile = localStorage.getItem('OnMobile') === 'true';
        const isLogin = currentRoute.startsWith('/login');
        const isActivation = currentRoute.startsWith('/activation');
        const isMaintenance = currentRoute.startsWith('/maintenance');
        this.isPreviewMode = currentRoute.startsWith('/preview-page');

        this.showFooter = !(isOnMobile || isLogin || isActivation || isMaintenance);
        this.showNavbar = !(isOnMobile && isActivation);
        this.gtmService.pushAngularVirtualPv(event.urlAfterRedirects)
      }
    });
  }

  protected toggleCollapsed(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  async ngOnInit() {
    const loggedIn = await this.keycloak.isLoggedIn();
    if (loggedIn) {
      const token = await this.keycloak.getToken();
      localStorage.setItem('authToken', JSON.stringify(token));
      const isMobile = localStorage.getItem('OnMobile') === 'true';
      const companyId = this.dataService.getCompanyId();

      if (isMobile && companyId === '45C3200100') {
        this.httpService.getCompanyIdsList().subscribe({
          next: (companies) => {
            if (!companies || companies.length === 0) {
              this.loadDefaultProfile();
              return;
            }
            if (companies.length === 1) {
              this.dataService.setCompanyId(companies[0].companyId);
              this.loadDefaultProfile();
              return;
            }
            this.openCompanySelectionModal(companies);
          },
          error: () => {
            console.error("Error");
            this.loadDefaultProfile(); 
          }
        });
        return;
      }
      this.loadDefaultProfile();
    }
    this.enableScrollToTopOnNavigation();
    this.cookieConsentService.loadCookieScript();
  }


  private loadDefaultProfile() {
    this.httpService.getUserProfileFromKeycloak().subscribe({
      next: (userData) => {
        this.authService.checkLoginStatus(userData, 'login');
      },
      error: () => {
        const textsModal = this.translateService.instant('error-mfa-login');
        this.openModalError(textsModal, 'error');
      },
    });
  }

  private openCompanySelectionModal(companies: any[]) {
    const modalRef = this.modal.create({
      nzTitle: this.translateService.instant('modal-companies.title'),
      nzContent: CompanySelectionModalComponent,
      nzData: { companies },
      nzClosable: false,
      nzFooter: null
    });
    modalRef.afterClose.subscribe((selectedCompanyId: string | null) => {
      if (selectedCompanyId) {
        this.dataService.setCompanyId(selectedCompanyId);
        this.loadDefaultProfile();
      }
    });
  }

  private openModalError(text: any, action?: string) {
      const modalRef: NzModalRef<ModalHandleLoginErrorsComponent> = this.modal.create({
        nzContent: ModalHandleLoginErrorsComponent,
        nzClosable: true,
        nzFooter: null,
        nzData: {
          dataModal: {
            texts: { title:text.title, content: text.content, button: text.button },
            hasTwoButtons: action === 'download' ? true : false,
            actionButton: () => {
            switch (action) {
              case 'error':
                this.storage.removeItem('authToken');
                this.keycloak.logout();
                break;
              default:
                break;
            }
            modalRef.close();
          },
          }
        }
      })
    }



  startSignalRConnection(): void {
   const user = JSON.parse(this.storage.getItem('userData')!) as UserAccountDTO;
   const individualId = user?.individualId;
   if (!individualId) return;
   const hubUrl = `${this.configService.baseUrl}/pushNotification?individualId=${individualId}`;
   this.connection = new signalR.HubConnectionBuilder()
     .withUrl(hubUrl)
     .withAutomaticReconnect()
     .build();
      console.log('2.SignalR startListeningToNotificationCenter CONNECTION ...');
   this.connection.on('pushNotification', () => {
      console.log('SignalR pushNotification refresh connected!!!');
      //alert('Ecco una notifica dal SERVER!!!!');
      this.ngZone.run(() => {
        AppComponent.onNotification.emit();
      })
   });
   this.connection
     .start()
     .then(() => console.log('Start connection MyClinic'))
     .catch((err) => console.error('SignalR connection error:', err));
 }

  private enableScrollToTopOnNavigation(): void {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
  }
}
